import { JANE_DEFINED_ROWS, MENU_ROW_TEMPLATES } from '@iheartjane/menu';
import { Flex, Modal, useToast } from '@iheartjane/reefer';
import {
  Form,
  FormValidationError,
  useForm,
} from '@iheartjane/reefer-hook-form';
import get from 'lodash-es/get';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { ConfirmWrapperWithTracking } from '@jane/business-admin/components/ConfirmWrapperWithTracking';
import type { ProductSearchSelection } from '@jane/business-admin/components/ProductSearchModalUpdated';
import { ProductSearchModalUpdated } from '@jane/business-admin/components/ProductSearchModalUpdated';
import { ScheduleCard } from '@jane/business-admin/components/shared/schedule/ScheduleCard';
import {
  DEFAULT_SCHEDULE,
  formatSchedule,
  parseSchedule,
} from '@jane/business-admin/components/shared/schedule/scheduleHelpers';
import { convert12HourTimeTo24HourTime } from '@jane/business-admin/components/shared/schedule/ScheduleTimeValidation';
import { foldRedundantExclusions } from '@jane/business-admin/components/shared/specials/modal/conditions/utils/foldRedundantExclusions';
import {
  useCreateMenuRow,
  useStoreSettings,
  useUpdateMenuRow,
} from '@jane/business-admin/data-access';
import {
  useCatchErrorsWithManager,
  useGetProductDetails,
  useModalActionsWithTracking,
  useStoreId,
} from '@jane/business-admin/hooks';
import type { MenuRow } from '@jane/business-admin/types';
import {
  ErrorReasons,
  EventNames,
  MODAL_CARD_WIDTH,
  ModalNames,
  normalizePath,
  parseValidationErrors,
  track,
} from '@jane/business-admin/util';
import type { EditedStoreMenuConfig } from '@jane/business-admin/util';
import type { NuProductType } from '@jane/shared/types';

import { DetailsCard } from './DetailsCard';
import type { FormValues } from './form.types';
import { FORM_ERROR_NAME } from './form.types';
import { MenuRowConditionsCard } from './MenuRowConditionsCard';
import { MenuRowProductTable } from './MenuRowProductTable';
import { ModalHeader } from './ModalHeader';
import { PosTagsCard } from './PosTagsCard';
import { ProductsCard } from './ProductsCard';
import { ThresholdsCard } from './ThresholdsCard';
import { ThresholdsConditionsCard } from './ThresholdsConditionsCard';
import { useProductsByApplyToMode } from './useProductsByApplyToMode';

type MenuRowProps = {
  allProductTypes: NuProductType[];
  closeModal: () => void;
  hasEmbeddedMenuRow?: boolean;
  productTypesLoaded: boolean;
  row?: MenuRow;
};

export const MenuRowModal = ({
  row,
  closeModal,
  hasEmbeddedMenuRow,
  productTypesLoaded,
  allProductTypes,
}: MenuRowProps) => {
  const isCreateMode = !row;

  const catchSubmitErrors = useCatchErrorsWithManager(
    `Error ${isCreateMode ? 'creating' : 'editing'} menu row. Please try again.`
  );
  const storeId = useStoreId();
  const { pathname } = useLocation();
  const toast = useToast();

  const { mutateAsync: createMenuRow, isSuccess: createSuccess } =
    useCreateMenuRow(storeId);
  const { mutateAsync: updateMenuRow, isSuccess: updateSuccess } =
    useUpdateMenuRow({ rowId: row?.id || 0, storeId });
  const { data: storePayload, isFetching: storeSettingsLoading } =
    useStoreSettings(storeId);

  const [totalNumberProductsSelected, setTotalNumberProductsSelected] =
    useState(row?.menu_products_ids?.all.length || 0);

  const initialApplyTo = getDefaultApplyTo({ isCreateMode, row });

  const {
    activeApplyToMode,
    selectedProductIds,
    setActiveApplyToMode,
    setProductsForMode,
    getProductsToSubmit,
  } = useProductsByApplyToMode({
    initialApplyTo: initialApplyTo,
    initialProductIds: row?.menu_products_ids?.all || [],
  });

  const {
    modalOpen: productSearchModalOpen,
    openModal: setProductSearchModalOpen,
    closeModal: setProductSearchModalClose,
  } = useModalActionsWithTracking(ModalNames.ProductSearch);

  const { data: productsForTable, isLoading: loadingProductsForTable } =
    useGetProductDetails(selectedProductIds, true);

  const productsForModal = useMemo(
    () =>
      productsForTable?.map<ProductSearchSelection & { inStock: boolean }>(
        ({ product_id, id, inventory_count }) => ({
          product_id: product_id.toString(),
          id,
          menu_product_id: id,
          inStock: inventory_count !== 0,
        })
      ) || [],
    [productsForTable]
  );

  const totalNumberProductsInStock = useMemo(
    () => productsForModal.filter((p) => p.inStock).length,
    [productsForModal]
  );

  const totalNumberProductsOutOfStock = useMemo(
    () => productsForModal.length - totalNumberProductsInStock,
    [productsForModal, totalNumberProductsInStock]
  );

  const rowProductType = useMemo(() => {
    if (!productTypesLoaded || isCreateMode) {
      return { display: {} };
    }

    const hasRootSubtype = get(
      row,
      'rules.includes.[0].kinds.[0].root_subtype',
      null
    );

    const matchingType = !hasRootSubtype
      ? allProductTypes?.find(
          ({ product_type }) =>
            product_type === get(row, 'rules.includes.[0].kinds.[0].kind', '')
        )
      : allProductTypes?.find(
          ({ product_subtype }) =>
            product_subtype ===
            get(row, 'rules.includes.[0].kinds.[0].root_subtype', '')
        );

    if (matchingType) {
      return {
        display: {
          category: matchingType?.product_type,
          subcategory: matchingType?.product_subtype,
        },
      };
    }

    return {
      display: {
        category: 'flower',
        subcategory: 'None',
      },
    };
  }, [isCreateMode, allProductTypes, row, productTypesLoaded]);

  const parsedSchedule = parseSchedule(
    {
      schedule: row?.schedule,
      startTime: row?.schedule?.start_time,
      endTime: row?.schedule?.end_time,
    },
    isCreateMode
  );

  const formMethods = useForm<FormValues>({
    defaultValues: {
      ...row,
      ...rowProductType,
      ...parsedSchedule,
      custom_display_name: row?.custom_display_name || row?.row_type,
      row_type: row?.row_type || 'custom',
      menu_row_type: row?.menu_row_type || 'custom',
      apply_to: initialApplyTo,
      display: {
        ...rowProductType.display,
        category: rowProductType.display?.category || 'flower',
        subcategory: rowProductType.display?.subcategory || 'None',
      },
      enabled: row?.enabled ?? false,
      rules: row?.rules || {
        includes: [],
        excludes: [],
      },
    },
  });

  const {
    formState: { isDirty, dirtyFields },
    watch,
  } = formMethods;

  const applyToWatch = watch('apply_to');
  const rowTypeValue = row
    ? row.menu_row_type
    : watch('menu_row_type') || watch('row_type');

  useEffect(() => {
    if (applyToWatch !== activeApplyToMode) {
      setActiveApplyToMode(applyToWatch);
    }
  }, [applyToWatch, activeApplyToMode]);

  const onSelectProducts = (products: ProductSearchSelection[]) => {
    track({
      event: EventNames.EditSelectedProducts,
      initial_product_count: totalNumberProductsSelected,
      final_product_count: products.length,
    });

    const productIds = products.map(({ id }) => id);
    setProductsForMode(productIds);
    setTotalNumberProductsSelected(products.length);
  };

  useEffect(() => {
    setTotalNumberProductsSelected(selectedProductIds.length);
  }, [selectedProductIds.length]);

  useEffect(() => {
    if (createSuccess || updateSuccess) {
      toast.add({
        label: `Menu row ${isCreateMode ? 'created' : 'updated'}!`,
        variant: 'success',
      });
      closeModal();
    }
  }, [createSuccess, updateSuccess, isCreateMode]);

  const onSubmit = (data: FormValues) => {
    const {
      display,
      custom_display_name: name,
      schedule,
      start_date,
      start_time,
      end_date,
      end_time,
      use_store_close_time,
      enabled,
      ...restData
    } = data;

    const includeScheduleOverride = data['has_schedule_overrides'] === true;
    const formattedSchedule = includeScheduleOverride
      ? formatSchedule(schedule)
      : DEFAULT_SCHEDULE;

    const menuRowType = data.menu_row_type || row?.menu_row_type || 'custom';

    if (
      menuRowType === 'pos_tags' &&
      !data.rules?.includes?.[0]?.pos_tags?.length
    ) {
      throw new FormValidationError(FORM_ERROR_NAME, [
        {
          name: 'rules.includes.0.pos_tags',
          message: 'Please fill out this field.',
        },
      ]);
    }

    const rowType = JANE_DEFINED_ROWS.includes(menuRowType || '')
      ? menuRowType
      : name;

    const requestData = {
      ...restData,
      custom_display_name: name ?? undefined,
      display,
      row_type: rowType!,
      menu_row_type: menuRowType,
      menu_products: getProductsToSubmit(),
      schedule: {
        ...formattedSchedule,
        end_date: end_date || null,
        enabled_date_end: convert12HourTimeTo24HourTime(end_time),
        start_date: start_date || null,
        enabled_date_start: convert12HourTimeTo24HourTime(start_time),
        use_store_close_time,
        use_store_close_time_for_end: use_store_close_time,
        enabled,
      },
    };

    if (applyToWatch === 'products') {
      requestData['rules'] = { includes: [], excludes: [] };
      requestData['applicability_rules'] = [];
    }

    if (applyToWatch === 'advanced') {
      if (restData.rules) {
        requestData['rules'] = foldRedundantExclusions(restData.rules);
      }

      const firstKind = data.rules?.includes?.[0]?.kinds?.[0];
      if (firstKind?.root_subtype) {
        requestData['applicability_rules'] = [
          {
            filter_type: 'root_subtype',
            filter_value: firstKind.root_subtype,
          },
        ];
      } else if (firstKind?.kind) {
        requestData['applicability_rules'] = [
          {
            filter_type: 'type',
            filter_value: firstKind.kind,
          },
        ];
      } else {
        requestData['applicability_rules'] = [];
      }
    }

    const trackSubmit = (eventProps: Partial<EditedStoreMenuConfig> = {}) => {
      track({
        event: EventNames.EditedStoreMenuConfig,
        action: isCreateMode ? 'create' : 'update',
        modal_name: isCreateMode
          ? ModalNames.CreateMenuRow
          : ModalNames.EditMenuRow,
        changed_attributes: Object.keys(dirtyFields),
        setting_name: 'menu row',
        successful: true,
        url: normalizePath(pathname, storeId),
        ...eventProps,
      });
    };

    const submitMethod = async () => {
      await (isCreateMode
        ? createMenuRow(requestData)
        : updateMenuRow(requestData));
      trackSubmit({ type: requestData.menu_row_type });
    };

    return catchSubmitErrors({
      submitMethod,
      requestData,
      onValidationError: (validationErrors: Record<string, unknown>) => {
        trackSubmit({
          successful: false,
          error_reason: ErrorReasons.InvalidParams,
        });
        throw new FormValidationError(
          FORM_ERROR_NAME,
          parseValidationErrors(validationErrors)
        );
      },
      callback: (errorBody) => {
        const serverError: string | undefined = errorBody?.errors?.error;

        if (
          serverError?.includes('duplicate') ||
          serverError?.includes('name has already been taken')
        ) {
          throw new FormValidationError(FORM_ERROR_NAME, [
            {
              name: 'custom_display_name',
              message:
                'A menu row already exists with that name. Please change it and try again.',
            },
          ]);
        }

        // Matches StoreCustomMenuRowSetting#one_new_products_row_per_store.
        if (serverError?.match(/only one new products row/i)) {
          trackSubmit({
            successful: false,
            error_reason: ErrorReasons.InvalidParams,
          });

          throw new Error(
            'This store already has a New Products row. Edit that row instead of adding another.'
          );
        }

        trackSubmit({
          successful: false,
          error_reason: ErrorReasons.ServerError,
        });

        throw new Error(
          `Error ${
            isCreateMode ? 'creating' : 'editing'
          } menu row. Please try again.`
        );
      },
    });
  };

  const shouldShowAddProducts =
    applyToWatch === 'products' &&
    !JANE_DEFINED_ROWS.includes(row?.menu_row_type || '');

  const shouldShowProductsCard = () => {
    if (!isCreateMode && row?.menu_row_type === 'specials') {
      return false;
    }

    if (MENU_ROW_TEMPLATES.includes(rowTypeValue ?? '')) {
      return false;
    }

    return applyToWatch === 'products' || !isCreateMode;
  };

  const shouldShowConditionsCardForRowType = () => {
    if (isCreateMode) {
      return !rowTypeValue || rowTypeValue === 'custom';
    }
    return rowTypeValue === 'custom';
  };

  const shouldShowThresholdConditions = () => {
    return rowTypeValue === 'new_products';
  };

  const shouldShowPosTagsCard = () => {
    return rowTypeValue === 'pos_tags';
  };

  const shouldShowSchedulingCard =
    !row || !JANE_DEFINED_ROWS.includes(row.menu_row_type || '');

  return (
    <>
      <ConfirmWrapperWithTracking
        open
        setOpen={closeModal}
        variant="full-screen"
        hasChanges={isDirty}
        modalName={
          isCreateMode ? ModalNames.CreateMenuRow : ModalNames.EditMenuRow
        }
      >
        <Form.BaseForm
          name={`${isCreateMode ? 'Create' : 'Edit'} menu row form`}
          formMethods={formMethods}
          onSubmit={onSubmit}
          formErrorName={FORM_ERROR_NAME}
        >
          <ModalHeader
            closeModal={closeModal}
            hasEmbeddedMenuRow={hasEmbeddedMenuRow}
            isCreateMode={isCreateMode}
            row={row}
            storeId={storeId}
          />
          <Modal.Content>
            <Flex justifyContent="center">
              <Flex width={MODAL_CARD_WIDTH} flexDirection="column" gap={24}>
                <Form.ErrorBanner name={FORM_ERROR_NAME} />

                <DetailsCard
                  isCreateMode={isCreateMode}
                  defaultRowType={row?.menu_row_type ?? undefined}
                  row={row}
                />

                {shouldShowConditionsCardForRowType() && (
                  <MenuRowConditionsCard
                    isLoading={storeSettingsLoading || !productTypesLoaded}
                  />
                )}
                {shouldShowThresholdConditions() && (
                  <>
                    <ThresholdsCard
                      isLoading={storeSettingsLoading || !productTypesLoaded}
                    />
                    <ThresholdsConditionsCard />
                  </>
                )}
                {shouldShowPosTagsCard() && <PosTagsCard />}
                {shouldShowProductsCard() && (
                  <ProductsCard
                    shouldShowAddProducts={shouldShowAddProducts}
                    onAddProductsClick={setProductSearchModalOpen}
                    productStockCounts={{
                      inStock: totalNumberProductsInStock,
                      outOfStock: totalNumberProductsOutOfStock,
                    }}
                    productsTable={
                      <MenuRowProductTable
                        isLoading={!isCreateMode && loadingProductsForTable}
                        selectedProducts={productsForTable}
                        totalNumberProductsSelected={
                          totalNumberProductsSelected
                        }
                      />
                    }
                  />
                )}
                {shouldShowSchedulingCard && (
                  <ScheduleCard
                    isLoading={storeSettingsLoading || !productTypesLoaded}
                    storePayload={storePayload}
                    variant="menu row"
                  />
                )}
              </Flex>
            </Flex>
          </Modal.Content>
        </Form.BaseForm>
      </ConfirmWrapperWithTracking>

      {productSearchModalOpen && (
        <ProductSearchModalUpdated
          closeModal={() => setProductSearchModalClose()}
          onSubmit={onSelectProducts}
          selectedProducts={productsForModal}
          onlyShowInStockProducts={false}
        />
      )}
    </>
  );
};

const getDefaultApplyTo = ({
  isCreateMode,
  row,
}: {
  isCreateMode: boolean;
  row?: MenuRow;
}) => {
  if (isCreateMode) {
    return 'advanced';
  }

  if (MENU_ROW_TEMPLATES.includes(row?.menu_row_type ?? '')) {
    return 'advanced';
  }

  const hasAdvancedRules =
    (row?.rules?.includes?.[0]?.kinds?.length ?? 0) > 0 ||
    (row?.rules?.includes?.[0]?.brands?.length ?? 0) > 0 ||
    row?.rules?.product_threshold;

  const hasIndividualProductsInRules =
    (row?.rules?.includes?.[0]?.product_ids?.length ?? 0) > 0;
  const hasMenuProductsWithoutRules =
    !hasAdvancedRules &&
    !hasIndividualProductsInRules &&
    (row?.menu_products_ids?.all?.length ?? 0) > 0;

  return hasIndividualProductsInRules || hasMenuProductsWithoutRules
    ? 'products'
    : 'advanced';
};
